import React, { useEffect, useState } from 'react';
import './MandiPriceMarket.css';

const MandiPriceMarket = () => {
  const [mandiData, setMandiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Filter states
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedCommodity, setSelectedCommodity] = useState('');
  const [selectedMarket, setSelectedMarket] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sorting
  const [sortField, setSortField] = useState('arrival_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);

  const fetchMandiData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://api.data.gov.in/resource/9ef84268-d588-465a-a308-a864a43d0070?api-key=579b464db66ec23bdd0000014fa11c3e7fb045f265941a3d91e9399d&format=json&limit=5000`
      );
      const data = await response.json();
      setMandiData(data.records || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Error fetching mandi data:", err);
      setMandiData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMandiData();
  }, []);

  // Get unique values for filters
  const uniqueStates = [...new Set(mandiData.map(item => item.state))].filter(Boolean).sort();
  const uniqueDistricts = [...new Set(
    mandiData
      .filter(item => !selectedState || item.state === selectedState)
      .map(item => item.district)
  )].filter(Boolean).sort();
  const uniqueCommodities = [...new Set(mandiData.map(item => item.commodity))].filter(Boolean).sort();
  const uniqueMarkets = [...new Set(
    mandiData
      .filter(item => !selectedState || item.state === selectedState)
      .filter(item => !selectedDistrict || item.district === selectedDistrict)
      .map(item => item.market)
  )].filter(Boolean).sort();

  // Apply filters
  const filteredData = mandiData.filter(item => {
    // State filter
    if (selectedState && item.state !== selectedState) return false;
    
    // District filter
    if (selectedDistrict && item.district !== selectedDistrict) return false;
    
    // Commodity filter
    if (selectedCommodity && item.commodity !== selectedCommodity) return false;
    
    // Market filter
    if (selectedMarket && item.market !== selectedMarket) return false;
    
    // Search filter - search across multiple fields
    if (searchTerm && searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase().trim();
      const searchableText = [
        item.commodity,
        item.state,
        item.district,
        item.market,
        item.variety,
        item.grade
      ].filter(Boolean).join(' ').toLowerCase();
      
      if (!searchableText.includes(search)) return false;
    }
    
    return true;
  });

  // Apply sorting
  const sortedData = [...filteredData].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (sortField === 'modal_price' || sortField === 'min_price' || sortField === 'max_price') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedState, selectedDistrict, selectedCommodity, selectedMarket, searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    // Scroll to top of table
    document.querySelector('.table-container')?.scrollIntoView({ behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSelectedState('');
    setSelectedDistrict('');
    setSelectedCommodity('');
    setSelectedMarket('');
    setSearchTerm('');
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // Calculate statistics with better data validation
  const validPrices = filteredData
    .map(item => parseFloat(item.modal_price))
    .filter(price => !isNaN(price) && price > 0 && price < 1000000); // Filter out invalid and extreme values

  const avgPrice = validPrices.length > 0
    ? Math.round(validPrices.reduce((sum, price) => sum + price, 0) / validPrices.length)
    : 0;
  
  const highestPrice = validPrices.length > 0
    ? Math.max(...validPrices)
    : 0;
  
  const lowestPrice = validPrices.length > 0
    ? Math.min(...validPrices)
    : 0;

  return (
    <div className="mandi-market-container">
      {/* Header */}
      <div className="mandi-header">
        <h1 className="mandi-title">
          🏪 Mandi Price Market
        </h1>
        <p className="mandi-subtitle">
          Live market prices from <strong>{uniqueStates.length} states</strong> across India • Updated Daily
        </p>
        <div className="mandi-stats">
          <div className="stat-box">
            <div className="stat-value">{filteredData.length.toLocaleString()}</div>
            <div className="stat-label">Market Entries</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">₹{avgPrice.toLocaleString()}</div>
            <div className="stat-label">Avg Price</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">₹{highestPrice.toLocaleString()}</div>
            <div className="stat-label">Highest</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">₹{lowestPrice.toLocaleString()}</div>
            <div className="stat-label">Lowest</div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-header">
          <h3>🔍 Filters</h3>
          <div className="filter-actions">
            <button onClick={clearFilters} className="clear-btn">
              Clear All
            </button>
            <button onClick={fetchMandiData} disabled={loading} className="refresh-btn">
              {loading ? '⏳ Loading...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔎 Search by commodity, state, district, market, or variety..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Filter Grid */}
        <div className="filters-grid">
          <div className="filter-group">
            <label>State</label>
            <select value={selectedState} onChange={(e) => {
              setSelectedState(e.target.value);
              setSelectedDistrict('');
              setSelectedMarket('');
            }}>
              <option value="">All States</option>
              {uniqueStates.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>District</label>
            <select value={selectedDistrict} onChange={(e) => {
              setSelectedDistrict(e.target.value);
              setSelectedMarket('');
            }} disabled={!selectedState}>
              <option value="">All Districts</option>
              {uniqueDistricts.map(district => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Commodity</label>
            <select value={selectedCommodity} onChange={(e) => setSelectedCommodity(e.target.value)}>
              <option value="">All Commodities</option>
              {uniqueCommodities.map(commodity => (
                <option key={commodity} value={commodity}>{commodity}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Market</label>
            <select value={selectedMarket} onChange={(e) => setSelectedMarket(e.target.value)}>
              <option value="">All Markets</option>
              {uniqueMarkets.map(market => (
                <option key={market} value={market}>{market}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="active-filters">
          {(selectedState || selectedDistrict || selectedCommodity || selectedMarket || searchTerm) && (
            <>
              <span className="filter-label">Active Filters:</span>
              {selectedState && <span className="filter-tag">{selectedState} <button onClick={() => setSelectedState('')}>×</button></span>}
              {selectedDistrict && <span className="filter-tag">{selectedDistrict} <button onClick={() => setSelectedDistrict('')}>×</button></span>}
              {selectedCommodity && <span className="filter-tag">{selectedCommodity} <button onClick={() => setSelectedCommodity('')}>×</button></span>}
              {selectedMarket && <span className="filter-tag">{selectedMarket} <button onClick={() => setSelectedMarket('')}>×</button></span>}
              {searchTerm && <span className="filter-tag">Search: "{searchTerm}" <button onClick={() => setSearchTerm('')}>×</button></span>}
            </>
          )}
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>📊 Showing {startIndex + 1}-{Math.min(endIndex, sortedData.length)} of {sortedData.length} entries (Total: {mandiData.length})</span>
        <span className="last-update">Data Source: Data.gov.in • 🔴 Live Data • Prices in ₹/Quintal</span>
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading mandi prices...</p>
          </div>
        ) : sortedData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No data found</h3>
            <p>Try adjusting your filters or refresh the data</p>
          </div>
        ) : (
          <table className="mandi-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('commodity')} className="sortable commodity-header">
                  Commodity {sortField === 'commodity' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('variety')} className="sortable">
                  Variety {sortField === 'variety' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('state')} className="sortable">
                  State {sortField === 'state' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('district')} className="sortable">
                  District {sortField === 'district' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('market')} className="sortable">
                  Market {sortField === 'market' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('min_price')} className="sortable price-col">
                  Min Price (₹/Qtl) {sortField === 'min_price' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('max_price')} className="sortable price-col">
                  Max Price (₹/Qtl) {sortField === 'max_price' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('modal_price')} className="sortable price-col highlight">
                  Modal Price (₹/Qtl) {sortField === 'modal_price' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={index}>
                  <td className="commodity-cell">
                    <span className="commodity-icon">🌾</span>
                    <strong>{item.commodity}</strong>
                  </td>
                  <td className="variety-cell">{item.variety || '-'}</td>
                  <td className="state-cell">{item.state}</td>
                  <td className="district-cell">{item.district}</td>
                  <td className="market-cell">{item.market}</td>
                  <td className="price-cell">₹{item.min_price}</td>
                  <td className="price-cell">₹{item.max_price}</td>
                  <td className="price-cell modal-price">₹{item.modal_price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && sortedData.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
          <div className="pagination-controls">
            <button 
              onClick={() => goToPage(1)} 
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              ⏮ First
            </button>
            <button 
              onClick={() => goToPage(currentPage - 1)} 
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              ← Previous
            </button>
            
            {/* Page numbers */}
            <div className="page-numbers">
              {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              onClick={() => goToPage(currentPage + 1)} 
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next →
            </button>
            <button 
              onClick={() => goToPage(totalPages)} 
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Last ⏭
            </button>
          </div>
          <div className="pagination-jump">
            <span>Go to page:</span>
            <input 
              type="number" 
              min="1" 
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= totalPages) {
                  goToPage(page);
                }
              }}
              className="page-input"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MandiPriceMarket;
